import { useRef } from "react";
import { motion, useInView } from "framer-motion";

// In the Press — editorial credibility band matching the Results section
// system: left-aligned mono eyebrow + large headline, then a 4-column row
// where each publication sits under its own top hairline (like the Results
// stat columns). Each publication keeps its OWN official quote. Logos are
// shown large and at full strength. Exact official Riversol content.
const PRESS = [
  {
    name: "Vogue",
    src: "https://www.riversol.com/cdn/shop/files/logo-vogue-home_2x_72a36642-e416-4cf8-af9a-d3fac1a3307f.png?v=1758833225&width=600",
    quote: "…a must-have for anyone serious about their skin.",
    h: "h-10 lg:h-14",
  },
  {
    name: "ELLE",
    src: "https://www.riversol.com/cdn/shop/files/logo-elle-home_2x_3f463620-960f-4ad2-ae89-9177fc70d2e7.png?v=1758833211&width=600",
    quote: "…a must-have for anyone serious about their skin.",
    h: "h-9 lg:h-12",
  },
  {
    name: "Forbes",
    src: "https://www.riversol.com/cdn/shop/files/logo-forbes-home_2x_c1bd3841-7fd3-4cab-9011-6256925f769e.png?v=1758833205&width=600",
    quote: "…consistently earning recognition from industry experts…",
    h: "h-8 lg:h-11",
  },
  {
    name: "Fast Company",
    src: "https://www.riversol.com/cdn/shop/files/logo-fastcompany-home_2x_adb8a1aa-b862-46f4-8737-23ebec18300d.png?v=1758833231&width=600",
    quote: "…their commitment to clean ingredients sets them apart…",
    h: "h-10 lg:h-14",
  },
];

export const PressStrip = ({ showHeading = true, showDividers = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section ref={ref} className="bg-[#EDF3F3] py-20 lg:py-28" data-testid="home-press">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500"
        >
          As seen in
        </motion.p>
        {showHeading && (
        <motion.h2
          initial={{ opacity: 0, y: 22 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.02em] text-neutral-900"
        >
          In the <span className="italic text-riversol-blue">press.</span>
        </motion.h2>
        )}

        <div className={`${showHeading ? "mt-8 lg:mt-10" : ""} grid grid-cols-2 lg:grid-cols-4 gap-x-8 lg:gap-x-14 gap-y-12`}>
          {PRESS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 22 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.18 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`press-${i}`}
              className={showDividers ? "group pt-6 border-t border-neutral-900/15" : "group"}
            >
              <div className="h-14 lg:h-16 flex items-center">
                <img
                  src={p.src}
                  alt={p.name}
                  className={`${p.h} w-auto object-contain transition-opacity duration-500 opacity-90 group-hover:opacity-100`}
                  style={{ filter: "brightness(0)" }}
                  loading="lazy"
                  draggable={false}
                  data-testid={`press-logo-${i}`}
                />
              </div>
              <p className="mt-6 text-[15px] lg:text-[16px] leading-relaxed text-neutral-700 max-w-[260px]">
                {p.quote}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PressStrip;
